import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  Avatar,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Instagram as InstagramIcon,
  Chat as ChatIcon,
  Comment as CommentIcon,
  Message as MessageIcon,
  VideoLibrary as VideoIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Reply as ReplyIcon,
  PriorityHigh as PriorityHighIcon,
  Category as CategoryIcon,
  Lightbulb as LightbulbIcon,
  ContentCopy as ContentCopyIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  TextSnippet as TemplateIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, isToday, isYesterday, isThisYear } from 'date-fns';
import { id } from 'date-fns/locale';
import { 
  Conversation, 
  Message, 
  Platform,
  InstagramCommentThread,
  InstagramDirectThread,
  TikTokCommentThread,
  ExternalCommentThread,
  MessageAttachment
} from '../types/conversation';
import { DmThread, DmMessage } from '../types/dm';
import { getDmThreads, getDmMessages, sendDmMessage, updateThreadStatus, markThreadAsRead, getUnreadCounts } from '../api/directMessages';
import { MessageTemplate, messageTemplatesAPI } from '../services/api/messageTemplates.service';
import TemplatePickerDialog from '../components/templates/TemplatePickerDialog';
import api from '../config/api';
import axios from 'axios';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`conversation-tabpanel-${index}`}
      aria-labelledby={`conversation-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const sentimentColors = {
  positive: 'success',
  negative: 'error',
  neutral: 'info',
} as const;

const priorityColors = {
  1: 'error', // High
  2: 'warning', // Medium
  3: 'success', // Low
} as const;

// Utility function for relative time formatting
const getRelativeTime = (date: Date | string): string => {
  const targetDate = new Date(date);
  const now = new Date();
  
  // If it's today, show relative time like "2 minutes ago"
  if (isToday(targetDate)) {
    const distance = formatDistanceToNow(targetDate, { addSuffix: true, locale: id });
    return distance;
  }
  
  // If it's yesterday, show "Yesterday at HH:mm"
  if (isYesterday(targetDate)) {
    return `Yesterday at ${format(targetDate, 'HH:mm')}`;
  }
  
  // If it's this year, show "dd MMM at HH:mm"
  if (isThisYear(targetDate)) {
    return format(targetDate, 'dd MMM \'at\' HH:mm', { locale: id });
  }
  
  // If it's a different year, show full date
  return format(targetDate, 'dd MMM yyyy \'at\' HH:mm', { locale: id });
};

interface Brand {
  brand_id: string;
  brand_name: string;
  platform: string;
}

const ConversationList: React.FC = (): React.ReactElement => {
  const theme = useTheme();
  const [selectedThread, setSelectedThread] = useState<DmThread | null>(null);
  const [threads, setThreads] = useState<DmThread[]>([]);
  const [messages, setMessages] = useState<DmMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [messageText, setMessageText] = useState('');
  const [replyToMessage, setReplyToMessage] = useState<DmMessage | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [userHasAllBrandsAccess, setUserHasAllBrandsAccess] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [refreshIndicatorTimeout, setRefreshIndicatorTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Template-related state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [suggestedTemplates, setSuggestedTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const response = await api.get(`/feedback/brands`);
      const brandsData = response.data.data as Brand[];
      const hasAllBrandsAccess = response.data.hasAllBrandsAccess;
      
      setBrands(brandsData.filter(brand => brand.platform === 'instagram'));
      setUserHasAllBrandsAccess(hasAllBrandsAccess);
      
      // Auto-select the first available option
      if (hasAllBrandsAccess) {
        // If user has all brands access, select "AB" (All Brands) by default
        setSelectedBrand('AB');
      } else if (brandsData.length > 0) {
        // Otherwise, select the first individual brand
        const instagramBrands = brandsData.filter(brand => brand.platform === 'instagram');
        if (instagramBrands.length > 0) {
          setSelectedBrand(instagramBrands[0].brand_id);
        }
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      setError('Failed to load brands. Please try again later.');
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const brandId = selectedBrand || (userHasAllBrandsAccess ? 'AB' : '');
      if (!brandId) return;
      
      const response = await getUnreadCounts(brandId);
      setUnreadCounts(response.data);
    } catch (error) {
      console.error('Error fetching unread counts:', error);
      // Don't show error to user as this is not critical
    }
  };

  // Modify fetchThreads to include brand_id
  const fetchThreads = async () => {
    setLoading(true);
    setError(null);
    try {
      const brandId = selectedBrand || (userHasAllBrandsAccess ? 'AB' : '');
      if (!brandId) {
        setThreads([]);
        setError('Please select a brand to view conversations.');
        return;
      }
      
      const response = await getDmThreads(brandId, page, 10, undefined, searchQuery || undefined);
      
      if (page === 1) {
        setThreads(response.data);
      } else {
        setThreads(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.pagination.page < response.pagination.pages);
    } catch (error: any) {
      console.error('Error fetching DM threads:', error);
      setError('Failed to load conversations. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch messages for a selected thread
  const fetchMessages = async (threadId: string, showLoadingState: boolean = true) => {
    if (showLoadingState) {
      setLoadingMessages(true);
    }
    setMessageError(null);
    try {
      const response = await getDmMessages(threadId);
      
      // Transform messages for display
      const transformedMessages = response.data.map((msg: DmMessage) => ({
        ...msg,
        isCustomer: msg.is_echo === false  // if is_echo is false, it's a customer message
      }));
      
      setMessages(transformedMessages);
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      // Only show error message if this is a manual fetch, not auto-refresh
      if (showLoadingState) {
        setMessageError('Failed to load messages. Please try again later.');
      }
    } finally {
      if (showLoadingState) {
        setLoadingMessages(false);
      }
    }
  };

  useEffect(() => {
    if (selectedBrand || userHasAllBrandsAccess) {
      fetchThreads();
      fetchUnreadCounts();
    }
  }, [page, searchQuery, selectedBrand]);

  // Auto-refresh current thread messages every 10 seconds
  useEffect(() => {
    if (selectedThread) {
      // Initial fetch with loading state
      fetchMessages(selectedThread.thread_id, true);
      
      // Set up auto-refresh interval (background fetches without loading state)
      const interval = setInterval(async () => {
        setIsAutoRefreshing(true);
        try {
          await fetchMessages(selectedThread.thread_id, false);
          // Also refresh unread counts to catch any new threads or messages
          await fetchUnreadCounts();
        } finally {
          // Hide the indicator after a short delay
          const timeout = setTimeout(() => setIsAutoRefreshing(false), 1000);
          setRefreshIndicatorTimeout(timeout);
        }
      }, 10000); // 10 seconds
      
      setAutoRefreshInterval(interval);
      
      // Cleanup function
      return () => {
        if (interval) {
          clearInterval(interval);
        }
        if (refreshIndicatorTimeout) {
          clearTimeout(refreshIndicatorTimeout);
        }
        setAutoRefreshInterval(null);
        setRefreshIndicatorTimeout(null);
        setIsAutoRefreshing(false);
      };
    } else {
      // Clear interval when no thread is selected
      if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
        setAutoRefreshInterval(null);
      }
      if (refreshIndicatorTimeout) {
        clearTimeout(refreshIndicatorTimeout);
        setRefreshIndicatorTimeout(null);
      }
      setIsAutoRefreshing(false);
    }
  }, [selectedThread?.thread_id]); // Only re-run when thread changes

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
      }
      if (refreshIndicatorTimeout) {
        clearTimeout(refreshIndicatorTimeout);
      }
    };
  }, []);

  const handleThreadClick = async (thread: DmThread) => {
    setSelectedThread(thread);
    // Note: fetchMessages will be called by the useEffect when selectedThread changes
    
    // Mark thread as read if it has unread messages
    if (thread.unread_count && thread.unread_count > 0) {
      try {
        await markThreadAsRead(thread.thread_id);
        
        // Update local unread counts
        setUnreadCounts(prev => ({
          ...prev,
          [thread.thread_id]: 0
        }));
        
        // Update thread unread count in the local state
        setThreads(prevThreads => 
          prevThreads.map(t => 
            t.thread_id === thread.thread_id 
              ? { ...t, unread_count: 0 }
              : t
          )
        );
      } catch (error) {
        console.error('Error marking thread as read:', error);
        // Don't show error to user as this is a background operation
      }
    }
    
    // Update thread status to 'in_progress' when opened
    try {
      await updateThreadStatus(thread.thread_id, 'in_progress');
      
      // Optionally refresh the threads list to get updated data from backend
      // or we could add a status field to the DmThread interface if needed
      // For now, we'll just ensure the thread remains active
      setThreads(prevThreads => 
        prevThreads.map(t => 
          t.thread_id === thread.thread_id 
            ? { ...t, is_active: true }
            : t
        )
      );
    } catch (error) {
      console.error('Error updating thread status:', error);
      // Don't show error to user as this is a background operation
    }
  };

  const handleCloseDialog = () => {
    setSelectedThread(null);
    setMessages([]);
    setReplyToMessage(null);
  };
  
  const handleSendMessage = async () => {
    if (!selectedThread || !messageText.trim()) return;
    
    try {
      // Send message with all necessary data including template info
      await sendDmMessage(
        selectedThread.thread_id,
        messageText,
        selectedThread.customer_id,
        replyToMessage?.message_id,
        undefined, // replyToStoryId
        undefined, // replyToType
        undefined, // attachments
        selectedTemplate?.id,
        templateVariables
      );
      
      // Clear message input and reply state
      setMessageText('');
      setReplyToMessage(null);
      setSelectedTemplate(null);
      setTemplateVariables({});
      
      // Refresh messages (manual refresh with loading state)
      fetchMessages(selectedThread.thread_id, true);
      
      // Show success message
      setSnackbarMessage('Message sent successfully');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Error sending message:', error);
      setMessageError('Failed to send message. Please try again.');
      setSnackbarMessage('Failed to send message');
      setSnackbarOpen(true);
    }
  };
  
  const handleReplyClick = (message: DmMessage) => {
    setReplyToMessage(message);
  };

  // Template handling functions
  const handleOpenTemplateDialog = () => {
    setTemplateDialogOpen(true);
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
  };

  const handleSelectTemplate = (template: MessageTemplate, processedContent: string, variables: Record<string, string>) => {
    setSelectedTemplate(template);
    setMessageText(processedContent);
    setTemplateVariables(variables);
    setTemplateDialogOpen(false);
  };

  const clearSelectedTemplate = () => {
    setSelectedTemplate(null);
    setTemplateVariables({});
  };
  
  const cancelReply = () => {
    setReplyToMessage(null);
  };
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  const handleLoadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  const getPlatformIcon = (platform: Platform) => {
    switch (platform) {
      case 'instagram_comment':
        return <InstagramIcon />;
      case 'instagram_dm':
        return <ChatIcon />;
      case 'tiktok_comment':
        return <VideoIcon />;
      case 'external':
        return <CommentIcon />;
      default:
        return <MessageIcon />;
    }
  };

  const renderAttachment = (attachment: any) => {
    if (!attachment) return null;
    
    switch (attachment.type) {
      case 'ig_reel':
      case 'video':
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Shared {attachment.type === 'ig_reel' ? 'Reel' : 'Video'}: {attachment.payload?.title || 'Media'}
            </Typography>
            {attachment.payload?.url && (
              <Box
                component="img"
                src={attachment.payload.url}
                alt={attachment.payload.title || 'Media'}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 1,
                  mt: 1
                }}
              />
            )}
          </Box>
        );
      case 'image':
      case 'photo':
        return (
          <Box sx={{ mt: 1 }}>
            {attachment.url && (
              <Box
                component="img"
                src={attachment.url}
                alt="Image"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 1,
                }}
              />
            )}
          </Box>
        );
      case 'sticker':
        return (
          <Box sx={{ mt: 1 }}>
            {attachment.url && (
              <Box
                component="img"
                src={attachment.url}
                alt="Sticker"
                sx={{
                  maxWidth: 100,
                  maxHeight: 100,
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
        );
      case 'audio':
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Audio Message
            </Typography>
            {attachment.url && (
              <Box
                component="audio"
                controls
                src={attachment.url}
                sx={{ width: '100%', mt: 1 }}
              />
            )}
          </Box>
        );
      default:
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {attachment.type} attachment
            </Typography>
          </Box>
        );
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'open':
        return (
          <Chip
            size="small"
            label="New"
            color="error"
            sx={{ mt: 0.5 }}
          />
        );
      case 'in_progress':
        return (
          <Chip
            size="small"
            label="In Progress"
            color="warning"
            sx={{ mt: 0.5 }}
          />
        );
      case 'done':
      case 'closed':
        return (
          <Chip
            size="small"
            label="Done"
            color="success"
            sx={{ mt: 0.5 }}
          />
        );
      default:
        return null;
    }
  };

  const renderSidebar = () => {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2 }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="brand-select-label">Select Brand</InputLabel>
            <Select
              labelId="brand-select-label"
              id="brand-select"
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setPage(1);
                setThreads([]);
              }}
              label="Select Brand"
              disabled={loadingBrands}
              startAdornment={
                <InputAdornment position="start">
                  <BusinessIcon />
                </InputAdornment>
              }
            >
              {userHasAllBrandsAccess && (
                <MenuItem value="AB">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography>All Brands</Typography>
                    <Chip
                      label="Full Access"
                      color="success"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </MenuItem>
              )}
              {brands.map((brand) => (
                <MenuItem key={brand.brand_id} value={brand.brand_id}>
                  {brand.brand_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {threads.length === 0 && !loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No conversations found</Typography>
            </Box>
          ) : (
            <>
              {threads.map((thread) => (
                <Box
                  key={thread.thread_id}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: selectedThread?.thread_id === thread.thread_id ? 'action.selected' : 
                            (thread.unread_count && thread.unread_count > 0 ? 'action.hover' : 'transparent'),
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderLeft: thread.unread_count && thread.unread_count > 0 ? 
                               `4px solid ${theme.palette.primary.main}` : 'none',
                    opacity: !thread.is_active ? 0.7 : 1,
                  }}
                  onClick={() => handleThreadClick(thread)}
                >
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: 'primary.main',
                          opacity: !thread.is_active ? 0.7 : 1,
                        }}
                      >
                        {thread.customer_username ? thread.customer_username.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                      {thread.unread_count && thread.unread_count > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            minWidth: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            border: '2px solid white',
                          }}
                        >
                          {thread.unread_count > 99 ? '99+' : thread.unread_count}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          noWrap 
                          sx={{ 
                            fontWeight: thread.unread_count && thread.unread_count > 0 ? 700 : 600,
                            color: thread.unread_count && thread.unread_count > 0 ? 'primary.main' : 'inherit'
                          }}
                        >
                          {thread.customer_username ? `@${thread.customer_username}` : thread.customer_id}
                        </Typography>
                        <Chip
                          label={thread.brand_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 20,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </Box>
                      {thread.last_message && (
                        <Typography 
                          variant="body2" 
                          color={thread.unread_count && thread.unread_count > 0 ? 'text.primary' : 'text.secondary'}
                          noWrap
                          sx={{ 
                            fontWeight: thread.unread_count && thread.unread_count > 0 ? 500 : 400 
                          }}
                        >
                          {thread.last_message.message_text || '[Media attachment]'}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {thread.last_message ? getRelativeTime(thread.last_message.timestamp) : 'No messages'}
                        </Typography>
                        {/* {getStatusChip(thread.is_active ? 'open' : 'closed')} */}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {hasMore && !loading && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="outlined" onClick={handleLoadMore}>
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  const renderChatArea = () => {
    if (!selectedThread) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <InstagramIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select a conversation to start messaging
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
              }}
            >
              {selectedThread.customer_username ? selectedThread.customer_username.charAt(0).toUpperCase() : '?'}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedThread.customer_username ? `@${selectedThread.customer_username}` : selectedThread.customer_id}
                </Typography>
                <Chip
                  label={selectedThread.brand_name}
                  size="small"
                  color="primary"
                  icon={<BusinessIcon />}
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Instagram Direct Message
                </Typography>
                {messages.filter(m => m.sentiment === 'negative').length > 0 && (
                  <Chip
                    label={`${messages.filter(m => m.sentiment === 'negative').length} Negative`}
                    color="error"
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusChip(selectedThread.is_active ? 'open' : 'closed')}
            <Typography variant="caption" color="text.secondary">
              Last activity: {selectedThread.last_activity ? getRelativeTime(selectedThread.last_activity) : 'Unknown'}
            </Typography>
            {autoRefreshInterval && (
              <Chip
                label={isAutoRefreshing ? "Refreshing..." : "Auto-refresh: ON"}
                color={isAutoRefreshing ? "warning" : "success"}
                size="small"
                sx={{ 
                  fontSize: '0.7rem', 
                  height: 20,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            )}
            <Tooltip title="Refresh messages">
              <IconButton
                size="small"
                onClick={() => fetchMessages(selectedThread.thread_id, true)}
                disabled={loadingMessages || isAutoRefreshing}
                sx={{ ml: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loadingMessages ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : messageError ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            <Alert severity="error">{messageError}</Alert>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column-reverse',
              gap: 2,
              bgcolor: 'background.default',
            }}
          >
            {messages.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">No messages yet</Typography>
              </Box>
            ) : (
              messages.map((message) => (
                <Box
                  key={message.message_id}
                  sx={{
                    display: 'flex',
                    flexDirection: message.isCustomer ? 'row' : 'row-reverse',
                    gap: 1,
                    maxWidth: '70%',
                    alignSelf: message.isCustomer ? 'flex-start' : 'flex-end',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: message.isCustomer ? 'primary.main' : 'secondary.main',
                    }}
                  >
                    {message.isCustomer && selectedThread.customer_username
                      ? selectedThread.customer_username.charAt(0).toUpperCase() 
                      : message.isCustomer ? '?' : 'CS'}
                  </Avatar>
                  <Box>
                    {message.reply_to_message_id && message.replied_to_message && (
                      <Paper
                        sx={{
                          p: 1,
                          mb: 0.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          color: 'text.secondary',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          Reply to:
                        </Typography>{' '}
                        {message.replied_to_message.message_text}
                      </Paper>
                    )}
                    
                    {message.reply_to_story_id && (
                      <Paper
                        sx={{
                          p: 1,
                          mb: 0.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          color: 'text.secondary',
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          Reply to Story
                        </Typography>
                        {message.reply_to_story_url && (
                          <Box
                            component="img"
                            src={message.reply_to_story_url}
                            alt="Story"
                            sx={{
                              display: 'block',
                              maxWidth: 100,
                              maxHeight: 100,
                              mt: 0.5,
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </Paper>
                    )}
                    
                    <Box sx={{ position: 'relative' }}>
                      {message.isCustomer && (
                        <Box sx={{ position: 'absolute', top: -24, left: 0, display: 'flex', gap: 0.5, zIndex: 1 }}>
                          {message.sentiment && (
                            <Chip
                              label={message.sentiment.charAt(0).toUpperCase() + message.sentiment.slice(1)}
                              color={sentimentColors[message.sentiment]}
                              size="small"
                            />
                          )}
                          {message.category && (
                            <Tooltip title="Category">
                              <Chip
                                icon={<CategoryIcon fontSize="small" />}
                                label={message.category}
                                size="small"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {message.priority && (
                            <Tooltip title="Priority">
                              <Chip
                                icon={<PriorityHighIcon fontSize="small" />}
                                label={`P${message.priority}`}
                                color={priorityColors[message.priority]}
                                size="small"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                      <Paper
                        sx={{
                          p: 1.5,
                          bgcolor: message.isCustomer ? 'grey.100' : 'primary.main',
                          color: message.isCustomer ? 'text.primary' : 'white',
                          borderRadius: 2,
                          position: 'relative',
                          borderColor: message.isCustomer && message.sentiment === 'negative' ? 'error.main' : 'transparent',
                          borderWidth: 1,
                          borderStyle: 'solid'
                        }}
                      >
                        {message.message_text && (
                          <Typography variant="body1">{message.message_text}</Typography>
                        )}
                        {message.suggested_reply && message.isCustomer && (
                          <Box sx={{ 
                            mt: 1, 
                            p: 1, 
                            bgcolor: 'action.hover', 
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1 
                          }}>
                            <LightbulbIcon 
                              fontSize="small" 
                              sx={{ color: 'info.main', mt: 0.5 }} 
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Suggested Reply
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                {message.suggested_reply}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => handleApplySuggestion(message.suggested_reply || '')}
                              >
                                Apply Suggestion
                              </Button>
                            </Box>
                          </Box>
                        )}
                        {message.attachments?.map((attachment, index) => (
                          <Box key={index}>
                            {renderAttachment(attachment)}
                          </Box>
                        ))}
                        
                        {message.isCustomer && (
                          <IconButton 
                            size="small" 
                            sx={{ 
                              position: 'absolute', 
                              right: -35, 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              opacity: 0.5,
                              '&:hover': { opacity: 1 }
                            }}
                            onClick={() => handleReplyClick(message)}
                          >
                            <ReplyIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Paper>
                      
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ 
                          display: 'block', 
                          mt: 0.5, 
                          textAlign: message.isCustomer ? 'left' : 'right',
                          fontSize: '0.7rem'
                        }}
                      >
                        {getRelativeTime(message.timestamp)}
                        {message.is_read && message.isCustomer && ' • Read'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}

        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          {replyToMessage && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 1, 
                mb: 1, 
                bgcolor: 'action.hover',
                borderRadius: 1
              }}
            >
              <ReplyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary' }}>
                Replying to: {replyToMessage.message_text}
              </Typography>
              <IconButton size="small" onClick={cancelReply}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          
          {selectedTemplate && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 1, 
                mb: 1, 
                bgcolor: 'primary.light',
                borderRadius: 1,
                color: 'primary.contrastText'
              }}
            >
              <TemplateIcon fontSize="small" sx={{ mr: 1 }} />
              <Typography variant="caption" sx={{ flex: 1 }}>
                Using template: {selectedTemplate.template_name}
              </Typography>
              <IconButton size="small" onClick={clearSelectedTemplate} sx={{ color: 'inherit' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          
          <TextField
            fullWidth
            placeholder="Type a message..."
            variant="outlined"
            size="small"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title="Use Template">
                    <IconButton 
                      size="small"
                      onClick={handleOpenTemplateDialog}
                      color="primary"
                    >
                      <TemplateIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!messageText.trim()}
                  >
                    <SendIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    );
  };

  const handleApplySuggestion = (suggestion: string) => {
    setMessageText(suggestion);
    // Focus the text input
    const textInput = document.querySelector<HTMLInputElement>('input[placeholder="Type a message..."]');
    if (textInput) {
      textInput.focus();
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex' }}>
      <Box sx={{ width: 350, borderRight: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InstagramIcon />
            Instagram Direct Messages
          </Typography>
        </Box>
        {renderSidebar()}
      </Box>
      <Box sx={{ flex: 1 }}>
        {renderChatArea()}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
      
      {selectedThread && (
        <TemplatePickerDialog
          open={templateDialogOpen}
          onClose={handleCloseTemplateDialog}
          onSelectTemplate={handleSelectTemplate}
          threadId={selectedThread.thread_id}
          brandId={selectedThread.brand_id}
          customerUsername={selectedThread.customer_username}
          suggestedTemplates={suggestedTemplates}
        />
      )}
    </Box>
  );
};

export default ConversationList; 